import {
  ChatHttpError,
  type ChatAttachment,
  type ChatClient,
  type ChatDelivery,
  type ChatMessage,
} from '../client';
import { splitLinks } from './links';
import { ATTACHMENT_ERROR_CLASS, ATTACHMENT_ICON_CLASS, createAttachmentCardElement } from './attachment';
import { clockIcon, spinnerIcon, warningIcon } from './icons';
import { getWidgetLocale, t, tf } from './i18n';

const ROOT_CLASS = 'flix-chat-widget__message';
const ATTACHMENT_INTERACTIVE_CLASS = `${ROOT_CLASS}-attachment-interactive`;

// A live-only notice (never persisted as a real message — see
// messages.ts's appendSystemNotice), so it's built directly rather than
// through createMessageElement/ChatMessage, which stay scoped to real,
// server-sourced messages only.
export function createStaffJoinedElement(staffName: string, avatarUrl: string | null): HTMLElement {
  const element = document.createElement('div');
  element.className = `${ROOT_CLASS} ${ROOT_CLASS}--system ${ROOT_CLASS}--staff-joined`;

  if (avatarUrl) {
    const avatar = document.createElement('img');
    avatar.className = `${ROOT_CLASS}-system-avatar`;
    avatar.src = avatarUrl;
    avatar.alt = '';
    element.append(avatar);
  }

  const body = document.createElement('span');
  body.textContent = tf('staffJoined', { name: staffName });
  element.append(body);

  return element;
}

type MessageAlignment = 'customer' | 'staff' | 'system';

function toMessageAlignment(senderType: string): MessageAlignment {
  switch (senderType) {
    case 'CUSTOMER':
      return 'customer';
    case 'STAFF':
      return 'staff';
    default:
      return 'system';
  }
}

function formatTimestamp(createdAt: string): string {
  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleTimeString(getWidgetLocale(), { hour: '2-digit', minute: '2-digit' });
}

// Fetches an attachment's bytes at most once per page load, regardless of
// how many times its card is clicked (or, for images, how many times it
// scrolls in and out of view) — keyed by attachment id, shared across every
// message element created during this session.
const objectUrlCache = new Map<string, string>();

async function resolveObjectUrl(client: ChatClient, attachment: ChatAttachment): Promise<string> {
  const cached = objectUrlCache.get(attachment.id);

  if (cached) {
    return cached;
  }

  const blob = await client.downloadAttachment(attachment.id);
  const url = URL.createObjectURL(blob);
  objectUrlCache.set(attachment.id, url);

  return url;
}

// A URL suitable for direct display in media elements and new tabs.
// R2-era attachments resolve to a short-lived signed storage URL — media
// elements and navigations need no CORS, so the widget has zero dependency
// on bucket CORS configuration. Legacy local-disk attachments (the /url
// endpoint rejects them with 400) fall back to the original authorized
// blob fetch through the API, which streams them directly. Signed URLs are
// deliberately never cached: they expire, and the browser's own cache
// handles repeat loads within a session.
async function resolveDisplayUrl(client: ChatClient, attachment: ChatAttachment): Promise<string> {
  try {
    const { url } = await client.getAttachmentUrl(attachment.id);
    return url;
  } catch (error) {
    if (error instanceof ChatHttpError && error.status === 400) {
      return resolveObjectUrl(client, attachment);
    }

    throw error;
  }
}

// Opens a full-screen lightbox for an already-resolved image URL. A fresh
// overlay per open — no persistent state to manage, closes on backdrop
// click, the close button, or Escape.
function openImageLightbox(url: string, alt: string): void {
  const overlay = document.createElement('div');
  overlay.className = 'flix-chat-widget__lightbox';

  const image = document.createElement('img');
  image.className = 'flix-chat-widget__lightbox-image';
  image.src = url;
  image.alt = alt;

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'flix-chat-widget__lightbox-close';
  closeButton.setAttribute('aria-label', t('closeImage'));
  closeButton.textContent = '×';

  function close(): void {
    overlay.remove();
    document.removeEventListener('keydown', handleKeyDown);
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      close();
    }
  }

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      close();
    }
  });
  closeButton.addEventListener('click', close);
  document.addEventListener('keydown', handleKeyDown);

  overlay.append(image, closeButton);
  document.body.append(overlay);
  closeButton.focus();
}

// Non-image attachments (PDF): a plain card; click (or Enter/Space) opens
// the file. R2-era files open in a new tab straight off the signed storage
// URL (inline disposition — the browser's own PDF viewer, with its save
// control, no CORS involved); legacy files resolve to a blob URL first.
function createFileAttachmentElement(client: ChatClient, attachment: ChatAttachment): HTMLElement {
  const card = createAttachmentCardElement(attachment.originalFilename, attachment.byteSize);

  card.classList.add(ATTACHMENT_INTERACTIVE_CLASS);
  card.setAttribute('role', 'button');
  card.setAttribute('tabindex', '0');
  card.setAttribute('aria-label', tf('downloadFile', { name: attachment.originalFilename }));

  let loading = false;

  async function handleActivate(): Promise<void> {
    if (loading) {
      return;
    }

    loading = true;

    // Opened synchronously inside the user gesture, then navigated once
    // the URL resolves — window.open after an await is what popup blockers
    // exist to stop. Not opened with the 'noopener' feature (that makes
    // window.open return null, losing the handle we navigate); the opener
    // link is severed manually instead.
    const viewer = window.open('', '_blank');

    if (viewer) {
      viewer.opener = null;
    }

    try {
      try {
        const { url } = await client.getAttachmentUrl(attachment.id);

        if (viewer) {
          viewer.location.replace(url);
          return;
        }

        // Popup blocked despite the gesture — a plain new-tab anchor gets
        // its own chance from the browser.
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener';
        link.click();
        return;
      } catch (error) {
        // Only a 400 means "legacy local-disk row, no direct URL" — fall
        // through to the original blob download. Anything else is a real
        // failure.
        if (!(error instanceof ChatHttpError && error.status === 400)) {
          throw error;
        }
      }

      // Legacy attachment: exactly the pre-R2 behavior — authorized blob
      // fetch through the API, then a download anchor. No viewer tab.
      viewer?.close();
      const url = await resolveObjectUrl(client, attachment);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.originalFilename;
      link.rel = 'noopener';
      link.click();
    } catch (error) {
      viewer?.close();
      console.error('Failed to load attachment.', error);
    } finally {
      loading = false;
    }
  }

  card.addEventListener('click', () => void handleActivate());
  card.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      void handleActivate();
    }
  });

  return card;
}

// Images: fetched as soon as the message renders, never gated behind a
// click — a click on the resolved image only ever opens the lightbox (the
// existing, untouched Image Viewer). While loading, this shows the exact
// same card component as a file attachment (just with a spinner glyph) so
// it reads as "loading", not as a broken/empty message; on failure it
// becomes a retry target, same shape again with a warning glyph.
function createImageAttachmentElement(client: ChatClient, attachment: ChatAttachment): HTMLElement {
  const card = createAttachmentCardElement(
    attachment.originalFilename,
    attachment.byteSize,
    spinnerIcon(),
  );
  card.setAttribute('aria-label', tf('loadingImage', { name: attachment.originalFilename }));

  // Turns a card into a manual-retry error state. Retry is always a click
  // away, never automatic — an automatic retry on failure would loop
  // forever against a permanently broken attachment.
  function toErrorCard(target: HTMLElement): void {
    target.classList.add(ATTACHMENT_ERROR_CLASS, ATTACHMENT_INTERACTIVE_CLASS);
    const icon = target.querySelector<HTMLElement>(`.${ATTACHMENT_ICON_CLASS}`);
    icon?.replaceChildren(warningIcon());
    target.setAttribute('role', 'button');
    target.setAttribute('tabindex', '0');
    target.setAttribute('aria-label', tf('retryLoadingImage', { name: attachment.originalFilename }));

    const retry = (): void => {
      target.replaceWith(createImageAttachmentElement(client, attachment));
    };

    target.addEventListener('click', retry, { once: true });
    target.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          retry();
        }
      },
      { once: true },
    );
  }

  resolveDisplayUrl(client, attachment)
    .then((url) => {
      const image = document.createElement('img');
      image.className = `${ROOT_CLASS}-attachment-image ${ATTACHMENT_INTERACTIVE_CLASS}`;
      image.src = url;
      image.alt = attachment.originalFilename;
      image.setAttribute('role', 'button');
      image.setAttribute('tabindex', '0');
      image.setAttribute('aria-label', tf('viewImage', { name: attachment.originalFilename }));

      // The URL resolved but the bytes themselves failed to arrive (e.g. a
      // signed URL past its expiry in a very long-lived tab) — swap to a
      // fresh manual-retry card rather than leaving a broken-image glyph.
      image.addEventListener('error', () => {
        const errorCard = createAttachmentCardElement(
          attachment.originalFilename,
          attachment.byteSize,
          warningIcon(),
        );
        toErrorCard(errorCard);
        image.replaceWith(errorCard);
      });

      const open = (): void => openImageLightbox(image.src, attachment.originalFilename);

      image.addEventListener('click', open);
      image.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      });

      card.replaceWith(image);
    })
    .catch((error: unknown) => {
      console.error('Failed to load an inline image attachment.', error);
      toErrorCard(card);
    });

  return card;
}

// Videos: the player streams straight from object storage via a
// short-lived signed URL (the SDK trades its session token for it — a
// <video> element can't send auth headers). preload="metadata" means only
// the header is fetched up front; playback then uses Range requests, so
// nothing downloads fully before playing. Same loading/error card pattern
// as images.
function createVideoAttachmentElement(client: ChatClient, attachment: ChatAttachment): HTMLElement {
  const card = createAttachmentCardElement(
    attachment.originalFilename,
    attachment.byteSize,
    spinnerIcon(),
  );
  card.setAttribute('aria-label', tf('loadingVideo', { name: attachment.originalFilename }));

  function showError(): void {
    card.classList.add(ATTACHMENT_ERROR_CLASS, ATTACHMENT_INTERACTIVE_CLASS);
    const icon = card.querySelector<HTMLElement>(`.${ATTACHMENT_ICON_CLASS}`);
    icon?.replaceChildren(warningIcon());
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    card.setAttribute('aria-label', tf('retryLoadingVideo', { name: attachment.originalFilename }));

    const retry = (): void => {
      card.replaceWith(createVideoAttachmentElement(client, attachment));
    };

    card.addEventListener('click', retry, { once: true });
    card.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          retry();
        }
      },
      { once: true },
    );
  }

  client
    .getAttachmentUrl(attachment.id)
    .then(({ url }) => {
      const video = document.createElement('video');
      video.className = `${ROOT_CLASS}-attachment-video`;
      video.src = url;
      video.controls = true;
      video.preload = 'metadata';
      // Inline playback on iOS instead of forcing fullscreen.
      video.playsInline = true;
      video.setAttribute('aria-label', attachment.originalFilename);

      card.replaceWith(video);
    })
    .catch((error: unknown) => {
      console.error('Failed to load a video attachment.', error);
      showError();
    });

  return card;
}

function createAttachmentElement(client: ChatClient, attachment: ChatAttachment): HTMLElement {
  if (attachment.mimeType.startsWith('image/')) {
    return createImageAttachmentElement(client, attachment);
  }

  if (attachment.mimeType.startsWith('video/')) {
    return createVideoAttachmentElement(client, attachment);
  }

  return createFileAttachmentElement(client, attachment);
}

// Callbacks the message element needs from the widget: opening the actions
// menu at a point, and toggling a reaction pill. Reactions on the pills and
// menu both route through onToggleReaction so add/remove stays in one place.
export interface MessageInteractions {
  onOpenMenu: (message: ChatMessage, x: number, y: number) => void;
  onToggleReaction: (message: ChatMessage, emoji: string) => void;
  // "Send again" on the customer's own message that did not reach support;
  // answers with the message as it now stands.
  onResend?: (message: ChatMessage) => Promise<ChatMessage>;
}

export const REACTIONS_CLASS = `${ROOT_CLASS}-reactions`;
const LONG_PRESS_MS = 450;

function replySenderLabel(senderType: string): string {
  return senderType === 'CUSTOMER' ? t('replyingToYou') : t('replyingToSupport');
}

// The small quoted block shown above a reply's own body — the sender it
// replies to plus a snippet, mirroring the Discord/WhatsApp reply pattern
// from the design mockup.
function createReplyQuote(reply: NonNullable<ChatMessage['replyTo']>): HTMLElement {
  const quote = document.createElement('div');
  quote.className = `${ROOT_CLASS}-reply-quote`;

  const who = document.createElement('span');
  who.className = `${ROOT_CLASS}-reply-quote-who`;
  who.textContent = replySenderLabel(reply.senderType);

  const snippet = document.createElement('span');
  snippet.className = `${ROOT_CLASS}-reply-quote-snippet`;
  snippet.textContent = reply.snippet;

  quote.append(who, snippet);
  return quote;
}

// (Re)builds the reaction pills for a message. Called at render time and on
// every live reaction update (see messages.ts's updateReactions).
export function renderReactionPills(
  container: HTMLElement,
  message: ChatMessage,
  interactions: MessageInteractions,
): void {
  const reactions = message.reactions ?? [];
  container.replaceChildren();
  container.hidden = reactions.length === 0;

  for (const reaction of reactions) {
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = `${ROOT_CLASS}-reaction-pill${
      reaction.mine ? ` ${ROOT_CLASS}-reaction-pill--mine` : ''
    }`;
    pill.setAttribute('aria-pressed', String(reaction.mine));

    const emoji = document.createElement('span');
    emoji.className = `${ROOT_CLASS}-reaction-emoji`;
    emoji.textContent = reaction.emoji;

    const count = document.createElement('span');
    count.className = `${ROOT_CLASS}-reaction-count`;
    count.textContent = String(reaction.count);

    pill.append(emoji, count);
    pill.addEventListener('click', () => interactions.onToggleReaction(message, reaction.emoji));
    container.append(pill);
  }
}

/**
 * Renders exactly one message bubble. Takes a ChatMessage from the Client
 * SDK — it has no idea whether it arrived via history or a live WebSocket
 * push — plus the same client, used only to lazily fetch an attachment's
 * bytes the first time its card is activated (click or Enter/Space).
 *
 * showAvatar controls whether the staff avatar renders for this message or
 * stays an invisible spacer — false for every message but the first in a
 * consecutive run from the same sender, so a multi-message reply reads as
 * one visual group instead of repeating the same avatar. messages.ts is the
 * only caller, and the one place that tracks "same sender as last time".
 *
 * interactions wires the actions menu (right-click / long-press) and the
 * reaction pills; omitted for the read-only history viewer.
 */
export const DELIVERY_STATUS_CLASS = `${ROOT_CLASS}-status`;
export const MESSAGE_META_CLASS = `${ROOT_CLASS}-meta`;

// The mark beside a customer's own message (see createMessageElement), also
// used to replace it live when a 'message_delivery' event arrives. resend,
// when given, offers "send again" on a message that did not reach support:
// it answers with the message's state afterwards, and the mark becomes that.
export function createDeliveryStatusElement(
  delivery: ChatDelivery,
  resend?: () => Promise<ChatDelivery | null>,
): HTMLElement {
  // ✓ (the last case below) only for 'delivered': support has it.
  const status = document.createElement('span');

  if (delivery === 'sending') {
    status.className = `${DELIVERY_STATUS_CLASS} ${DELIVERY_STATUS_CLASS}--sending`;
    status.setAttribute('role', 'img');
    status.setAttribute('aria-label', t('deliverySending'));
    status.append(clockIcon());
    return status;
  }

  // It may be with support: no "send again", so it never arrives twice.
  if (delivery === 'review') {
    status.className = `${DELIVERY_STATUS_CLASS} ${DELIVERY_STATUS_CLASS}--review`;
    const label = document.createElement('span');
    label.textContent = t('deliveryReview');
    status.append(clockIcon(), label);
    return status;
  }

  if (delivery === 'failed') {
    status.className = `${DELIVERY_STATUS_CLASS} ${DELIVERY_STATUS_CLASS}--failed`;
    const label = document.createElement('span');
    label.textContent = t('deliveryFailed');
    status.append(warningIcon(), label);

    if (resend) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `${ROOT_CLASS}-resend`;
      button.textContent = t('resendMessage');
      button.addEventListener('click', () => {
        // One press, one request: the server takes it up only if it is
        // still failed, so a second press could not send it twice anyway.
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        resend().then(
          (next) => status.replaceWith(createDeliveryStatusElement(next ?? 'sending', resend)),
          () => {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          },
        );
      });
      status.append(button);
    }

    return status;
  }

  status.className = `${DELIVERY_STATUS_CLASS} ${DELIVERY_STATUS_CLASS}--sent`;
  status.setAttribute('role', 'img');
  status.setAttribute('aria-label', t('deliveryDelivered'));
  status.textContent = '✓';
  return status;
}

// "Send again" for one message, where the widget offers it (never in the
// read-only view of a past conversation).
export function resendFor(
  message: ChatMessage,
  interactions?: MessageInteractions,
): (() => Promise<ChatDelivery | null>) | undefined {
  const onResend = interactions?.onResend;
  return onResend ? () => onResend(message).then((updated) => updated.delivery ?? null) : undefined;
}

export function createMessageElement(
  message: ChatMessage,
  client: ChatClient,
  showAvatar: boolean,
  interactions?: MessageInteractions,
): HTMLElement {
  const alignment = toMessageAlignment(message.senderType);

  const element = document.createElement('div');
  element.className = `${ROOT_CLASS} ${ROOT_CLASS}--${alignment}`;
  element.dataset.messageId = message.id;

  if (alignment === 'staff') {
    const avatar = document.createElement('div');
    avatar.className = `${ROOT_CLASS}-avatar${showAvatar ? ` ${ROOT_CLASS}-avatar--visible` : ''}`;
    avatar.textContent = t('staffInitial');
    element.append(avatar);
  }

  const group = document.createElement('div');
  group.className = `${ROOT_CLASS}-group`;

  if (message.replyTo) {
    group.append(createReplyQuote(message.replyTo));
  }

  const body = document.createElement('div');
  body.className = `${ROOT_CLASS}-body`;
  // Plain text, with any web address in it made a link (see links.ts) —
  // support replies point at products, guides and the refund policy.
  for (const run of splitLinks(message.body)) {
    if (run.href) {
      const link = document.createElement('a');
      link.href = run.href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      // An address reads left to right even inside an Arabic sentence.
      link.dir = 'ltr';
      link.textContent = run.text;
      body.append(link);
    } else {
      body.append(document.createTextNode(run.text));
    }
  }
  group.append(body);

  if (message.attachments.length > 0) {
    const attachments = document.createElement('div');
    attachments.className = `${ROOT_CLASS}-attachments`;

    for (const attachment of message.attachments) {
      attachments.append(createAttachmentElement(client, attachment));
    }

    group.append(attachments);
  }

  const meta = document.createElement('div');
  meta.className = `${ROOT_CLASS}-meta`;

  const timestamp = document.createElement('time');
  timestamp.className = `${ROOT_CLASS}-timestamp`;
  timestamp.dateTime = message.createdAt;
  timestamp.textContent = formatTimestamp(message.createdAt);
  meta.append(timestamp);

  // A reply the support assistant wrote says so, beside its time.
  if (message.automated) {
    const automated = document.createElement('span');
    automated.className = `${ROOT_CLASS}-automated`;
    automated.textContent = t('automatedReply');
    meta.append(automated);
  }

  // A customer's own message says whether it has reached support: a clock
  // while it is on its way, ✓ only once support's channel has it, "not
  // delivered" with "send again" when it did not get through, and "being
  // checked" when that is not known. Where nothing is known (an older
  // message with no Discord copy on record, or an older server that says
  // nothing), no mark at all. Nothing here ever claims "read" — Discord
  // never tells us a member of staff saw it.
  if (alignment === 'customer' && message.delivery) {
    meta.append(createDeliveryStatusElement(message.delivery, resendFor(message, interactions)));
  }

  group.append(meta);

  // Reaction pills row (below the meta), populated now and refreshed on
  // live reaction updates.
  if (interactions) {
    const reactionsContainer = document.createElement('div');
    reactionsContainer.className = REACTIONS_CLASS;
    renderReactionPills(reactionsContainer, message, interactions);
    group.append(reactionsContainer);
  }

  element.append(group);

  // System messages (a staff-joined notice etc.) are never interactive.
  if (interactions && alignment !== 'system') {
    wireMessageMenuTrigger(element, message, interactions);
  }

  return element;
}

// Opens the actions menu on right-click (desktop) or long-press (touch),
// anchored at the pointer. A long-press that turns into a scroll is
// cancelled by pointermove/pointerup before the timer fires.
function wireMessageMenuTrigger(
  element: HTMLElement,
  message: ChatMessage,
  interactions: MessageInteractions,
): void {
  element.addEventListener('contextmenu', (event) => {
    event.preventDefault();
    interactions.onOpenMenu(message, event.clientX, event.clientY);
  });

  let longPressTimer: ReturnType<typeof setTimeout> | null = null;
  const cancel = (): void => {
    if (longPressTimer !== null) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  };

  element.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'touch') {
      return;
    }
    cancel();
    longPressTimer = setTimeout(() => {
      longPressTimer = null;
      interactions.onOpenMenu(message, event.clientX, event.clientY);
    }, LONG_PRESS_MS);
  });
  element.addEventListener('pointermove', cancel);
  element.addEventListener('pointerup', cancel);
  element.addEventListener('pointercancel', cancel);
}
